import { useState } from "react";
import { format } from "date-fns";
import { FileText } from "lucide-react";
import { Card, CardContent, CardFooter } from "../../../../../components/ui/card";
import { Badge } from "../../../../../components/ui/badge";
import { Separator } from "../../../../../components/ui/separator";
import EmptyTabState from "../../../../../shared/ui/EmptyTabState";
import MarkdownRenderer from "../../../../../components/agno/MarkdownRenderer";
import ObjectDetailSheet from "./ObjectDetailSheet";

type Props = {
  records: any[];
  searchQuery?: string;
};

function safeFormat(value: any, fmt: string): string {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return format(d, fmt);
  } catch {
    return "";
  }
}

function matchesSearch(record: any, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  const haystack = [
    record.title, record.native_title,
    record.content_text, record.native_content_text,
    record.author_email, record.native_author_email,
    record.owner_email, record.native_owner_email,
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(q);
}

function getField(record: any, ...keys: string[]): string {
  for (const k of keys) {
    if (record[k]) return record[k];
  }
  return "";
}

const NoteCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  const title = getField(record, "title", "native_title") || "(Untitled note)";
  const content = getField(record, "content_text", "native_content_text");
  const authorEmail = getField(record, "author_email", "native_author_email", "owner_email", "native_owner_email");
  const authorName = authorEmail
    ? authorEmail.split("@")[0]?.replace(/[._]/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())
    : "";
  const date = safeFormat(
    record.created_at_tz || record.native_created_at_tz || record.created_at,
    "MMMM d, yyyy",
  );
  const source = getField(record, "source", "native_source");
  const avatarInitial = (authorName || authorEmail || "?").slice(0, 1).toUpperCase();

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow h-full flex flex-col"
      onClick={onClick}
    >
      <CardContent className="p-4 flex-1 space-y-2">
        {source && (
          <Badge variant="outline" className="text-[10px] uppercase">
            {source}
          </Badge>
        )}
        <p className="text-[13px] font-semibold text-foreground line-clamp-2">
          {title}
        </p>
        <div className="text-[13px] text-muted-foreground line-clamp-3">
          {content ? (
            <MarkdownRenderer>{content.slice(0, 200)}</MarkdownRenderer>
          ) : (
            <span className="italic">This note has no content.</span>
          )}
        </div>
      </CardContent>

      <Separator />

      <CardFooter className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-semibold">
            {avatarInitial}
          </div>
          <span className="text-xs text-muted-foreground font-medium truncate max-w-[140px]">
            {authorName || authorEmail || "Unknown"}
          </span>
        </div>
        {date && (
          <span className="text-xs text-muted-foreground">{date}</span>
        )}
      </CardFooter>
    </Card>
  );
};

const NotesView = ({ records, searchQuery = "" }: Props) => {
  const [selected, setSelected] = useState<any>(null);
  const filtered = records.filter((r) => matchesSearch(r, searchQuery));

  if (filtered.length === 0) {
    return (
      <EmptyTabState
        title="No notes yet"
        description={searchQuery ? "Try adjusting your search query." : "Notes associated with this account will appear here."}
        icon={<FileText size={24} />}
      />
    );
  }

  const detailTitle = getField(selected || {}, "title", "native_title") || "(Untitled note)";
  const detailContent = getField(selected || {}, "content_text", "native_content_text");
  const detailHtml = getField(selected || {}, "content_html", "native_content_html");

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
        {filtered.map((record: any) => (
          <NoteCard
            key={record.id || record.external_id || record.native_external_id || Math.random()}
            record={record}
            onClick={() => setSelected(record)}
          />
        ))}
      </div>

      <ObjectDetailSheet
        open={!!selected}
        onClose={() => setSelected(null)}
        title={detailTitle}
        subtitle={getField(selected || {}, "source", "native_source")}
        sourceUrl={getField(selected || {}, "source_url", "native_source_url")}
        fields={selected ? [
          { label: "Author", value: getField(selected, "author_email", "native_author_email") },
          { label: "Owner", value: getField(selected, "owner_email", "native_owner_email") },
          {
            label: "Created",
            value: selected.created_at_tz || selected.native_created_at_tz || selected.created_at,
            type: "date" as const,
          },
          {
            label: "Updated",
            value: selected.updated_at_tz || selected.native_updated_at_tz || selected.updated_at,
            type: "date" as const,
          },
        ] : []}
        bodyMarkdown={detailContent || detailHtml || ""}
        bodyLabel="Note Content"
      />
    </>
  );
};

export default NotesView;
